import React from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import { MAIN_CONTENT_ID } from 'components/skip-to-main/constants';
import { DesktopOnly, MobileOnly } from 'shared/components';
import { FilterPills, FilterPillsOverflowScrollContainer } from 'components/filter-pills/filter-pills';
import { useSortDispensariesByOpenStatus } from 'hooks/use-sort-dispensaries-by-open-status';

import { FiltersSidebar } from './components/filters-sidebar';
import { DispensariesSort } from './components/dispensaries-sort';
import { DispensariesEmptyState } from './components/dispensaries-empty-state';
import { DispensaryCardRedesign } from './components/dispensary-card-redesign/dispensary-card-redesign';
import { DispensariesLoadingStateRedesign } from './components/dispensaries-loading-state-redesign/dispensaries-loading-state-redesign';

function DispensaryGrid({ dispensaries }) {
  const sorted = useSortDispensariesByOpenStatus(dispensaries);
  return (
    <Grid id={MAIN_CONTENT_ID}>
      {_.map(sorted, (dispensary) => (
        <DispensaryCardRedesign key={dispensary?.id} dispensary={dispensary} />
      ))}
    </Grid>
  );
}

export default function DispensariesRedesign({ dispensaries, dispensariesLoading, filterPillOptions, heading }) {
  const showList = !dispensariesLoading && !_.isEmpty(dispensaries);
  const showEmpty = !dispensariesLoading && _.isEmpty(dispensaries);

  return (
    <PageWrapper>
      {/* Page title bar */}
      <TitleBar>
        <TitleInner>
          <PageTitle suppressHydrationWarning>{heading}</PageTitle>
          {!dispensariesLoading && (
            <ResultCount>
              {dispensaries.length > 0
                ? `${dispensaries.length} dispensar${dispensaries.length === 1 ? `y` : `ies`}`
                : null}
            </ResultCount>
          )}
        </TitleInner>
      </TitleBar>

      {/* Mobile: filter pills */}
      <MobileOnly>
        <FilterPillsOverflowScrollContainer mt={0} mb={0} ml={0}>
          <MobileFilterBar>
            <FilterPills filterPillOptions={filterPillOptions} />
          </MobileFilterBar>
        </FilterPillsOverflowScrollContainer>
      </MobileOnly>

      {/* Desktop: sidebar + grid */}
      <DesktopOnly>
        <ContentRow>
          <Sidebar>
            <SidebarCard>
              <FiltersSidebar />
            </SidebarCard>
          </Sidebar>

          <MainArea>
            <SortRow>
              <DispensariesSort />
            </SortRow>
            {dispensariesLoading && <DispensariesLoadingStateRedesign />}
            {showList && <DispensaryGrid dispensaries={dispensaries} />}
            {showEmpty && <DispensariesEmptyState />}
          </MainArea>
        </ContentRow>
      </DesktopOnly>

      {/* Mobile: single column */}
      <MobileOnly>
        <MobileContent>
          <SortRow>
            <DispensariesSort displaySortLabel={false} />
          </SortRow>
          {dispensariesLoading && <DispensariesLoadingStateRedesign />}
          {showList && <DispensaryGrid dispensaries={dispensaries} />}
          {showEmpty && <DispensariesEmptyState />}
        </MobileContent>
      </MobileOnly>
    </PageWrapper>
  );
}

/* ---- Styled components ---- */

const PageWrapper = styled.div`
  background: var(--rds-bg);
  min-height: 100vh;
  color: var(--rds-cream);
`;

const TitleBar = styled.div`
  background: var(--rds-bg-deep);
  border-bottom: 1px solid var(--rds-border);
  padding: 28px 32px 24px;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    padding: 20px 20px 16px;
  }
`;

const TitleInner = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: baseline;
  gap: 14px;
`;

const PageTitle = styled.h1`
  margin: 0;
  font-size: clamp(22px, 3vw, 32px);
  font-weight: 700;
  color: var(--rds-cream);
  letter-spacing: -0.5px;
`;

const ResultCount = styled.span`
  font-size: 15px;
  color: var(--rds-cream-dim);
  font-weight: 400;
`;

const MobileFilterBar = styled.div`
  padding: 14px 20px 10px;
  background: var(--rds-bg-deep);
  border-bottom: 1px solid var(--rds-border);
`;

const ContentRow = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 32px 60px;
  display: flex;
  align-items: flex-start;
  gap: 28px;
`;

const Sidebar = styled.aside`
  flex: 0 0 260px;
  position: sticky;
  top: 80px;
`;

const SidebarCard = styled.div`
  background: var(--rds-bg-card);
  border: 1px solid var(--rds-border);
  border-radius: 14px;
  overflow: hidden;

  /* Section headings */
  && h4,
  && h5,
  && [class*='Heading'],
  && [class*='heading'] {
    color: var(--rds-cream);
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  /* Section dividers */
  && [class*='Section']:not(:last-child) {
    border-bottom: 1px solid var(--rds-border);
  }

  /* Inner padding */
  && > div > div {
    padding: 20px 20px;
  }

  /* Text labels */
  && label,
  && span,
  && p {
    color: var(--rds-cream-dim);
    font-size: 14px;
  }

  /* Checkboxes, radio buttons — plum from theme */
  && .MuiCheckbox-root svg,
  && .MuiRadio-root svg {
    fill: var(--rds-cream-dim);
  }

  /* Order type buttons */
  && button {
    color: var(--rds-cream);
  }
`;

const MainArea = styled.main`
  flex: 1;
  min-width: 0;
`;

const SortRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 16px;

  /* Invert sort dropdown colors for dark bg */
  && select,
  && [class*='Select'],
  && [class*='select'] {
    color: var(--rds-cream);
    background: var(--rds-bg-card);
    border-color: var(--rds-border);
  }

  && label,
  && span {
    color: var(--rds-cream-dim);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  ${({ theme }) => theme.breakpoints.down(`sm`)} {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    grid-template-columns: 1fr;
  }
`;

const MobileContent = styled.div`
  padding: 20px 20px 60px;
`;
